import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useMe, useUpdateMe } from '@/features/session/api';
import { useLocaleStore } from '@/stores/locale';
import { SUPPORTED_LOCALES, type Locale } from '@/lib/i18n';
import { cn } from '@/lib/cn';

export function LanguageSwitcher({ syncToProfile = true }: { syncToProfile?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { locale, setLocale } = useLocaleStore();
  const { data: me } = useMe(syncToProfile);
  const updateMe = useUpdateMe();

  function handleSelect(next: Locale) {
    setLocale(next);
    if (syncToProfile && me) updateMe.mutate({ locale: next });
    setOpen(false);
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="grid h-8 w-8 place-items-center border border-border text-muted hover:bg-surface-2"
          aria-label={t('topbar.language')}
        >
          <Languages size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-44 border border-border bg-surface p-1.5 shadow-md">
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => handleSelect(l)}
              className={cn(
                'flex w-full items-center justify-between px-2.5 py-2 text-left text-sm hover:bg-surface-2',
                l === locale && 'font-semibold text-accent-ink',
              )}
            >
              {t(`language.${l}`)}
              {l === locale ? <Check size={14} /> : null}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
